// src/pages/components/Navbar.js

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

// Material UI
import Tooltip from '@mui/material/Tooltip'
import BackupIcon from '@mui/icons-material/Backup'
import StorageIcon from '@mui/icons-material/Storage'
import GridViewIcon from '@mui/icons-material/GridView'
import SettingsIcon from '@mui/icons-material/Settings'

// Local CSS
import styles from './Navbar.module.css'

/**
 * Navbar – a site‑wide navigation bar with GigemAI branding and route links.
 * 
 * Features:
 * - Centered company logo linking to home.
 * - Tooltip‑wrapped icon buttons for navigation:
 *     • Upload (BackupIcon) → `/upload`
 *     • Inventory (StorageIcon) → `/inventory`
 *     • Dataframe (GridViewIcon) → `/dataframe`
 *     • Settings (SettingsIcon) → `/settings`
 * - Uses React Router’s `<Link>` for client‑side routing.
 * - Material UI `Tooltip` for accessible hover labels.
 * 
 * No props are accepted; this component relies on fixed routes.
 * 
 * Author: Galo Alonso Peralta Espinoza
 * Date: 04-17-2025
 */

const Navbar = () => {
  const [showIcons, setShowIcons] = useState(false)

  useEffect(() => {
    
    const already = sessionStorage.getItem('navbarAnimated')
    if (!already) {

      setTimeout(() => setShowIcons(true), 100)
      sessionStorage.setItem('navbarAnimated', 'true')

    } else {
      setShowIcons(true)
    }
  }, [])

  return (
    <header className={styles.navbar}>
      <motion.div className={styles.navbarContent} layout>
        {/* Logo will animate from center → left */}
        <motion.img
          layout
          className={styles.navLogo}
          src="gigemai_logo_v2.png"
          alt="GigemAI Logo"
        />

        {showIcons && (
          <>
            <motion.div
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <Tooltip title="Upload" arrow>
                <Link to="/upload" className={styles.navButton}>
                  <BackupIcon className={styles.navIcon} />
                </Link>
              </Tooltip>
            </motion.div>

            <motion.div
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <Tooltip title="Inventory" arrow>
                <Link to="/inventory" className={styles.navButton}>
                  <StorageIcon className={styles.navIcon} />
                </Link>
              </Tooltip>
            </motion.div>

            <motion.div
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              <Tooltip title="Dataframe" arrow>
                <Link to="/dataframe" className={styles.navButton}>
                  <GridViewIcon className={styles.navIcon} />
                </Link>
              </Tooltip>
            </motion.div>

            <motion.div
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <Tooltip title="Settings" arrow>
                <Link to="/settings" className={styles.navButton}>
                  <SettingsIcon className={styles.navIcon} />
                </Link>
              </Tooltip>
            </motion.div>
          </>
        )}
      </motion.div>
    </header>
  )
}

export default Navbar